import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useDate,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TProjectManagement } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TBaseFilePO, TRecordAction } from '@/core/types'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TProjectManagement>({})
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)
  const { zonedTimeToUtc } = useDate()
  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    order_by: 'created_at,desc',
  })
  const customerFilters = ref<ToQueryStringFilters>({
    param: '',
    name: '',
    order_by: 'created_at,desc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['name', 'customer.name'],
  })
  const customerQueryParams = useToQueryString(toReactive(customerFilters), {
    searchColumns: ['name'],
  })

  const modulePagination = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: modulePagination.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      customersCatalog: customerQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      loadRecord: false,
      customersCatalog: true,
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at'],
  })

  const onAdd = async (payload: TProjectManagement) => {
    await moduleQuery.addRecord.mutateAsync({
      ...payload,
      delivery_deadline: payload.delivery_deadline
        ? zonedTimeToUtc(new Date(payload.delivery_deadline ?? ''), {
            removeTimeZone: true,
          })
        : payload.delivery_deadline,
    })
  }

  const onEdit = async (payload: TProjectManagement) => {
    await moduleQuery.editRecord.mutateAsync({
      ...payload,
      delivery_deadline: payload.delivery_deadline
        ? zonedTimeToUtc(new Date(payload.delivery_deadline ?? ''), {
            removeTimeZone: true,
          })
        : payload.delivery_deadline,
    })
  }

  const onDeleteMany = async () => {
    await moduleQuery.deleteRecords.mutateAsync(rows.value)
  }

  const onDelete = async (payload: TProjectManagement) => {
    await moduleQuery.deleteRecord.mutateAsync(payload)
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onUploadPO = async (payload: TProjectManagement & TBaseFilePO) => {
    await moduleQuery.actionUploadPo.mutateAsync(payload)
  }

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }
  const onUpdateProject = (payload: TProjectManagement) => {
    moduleQuery.serviceHandlers.updateRecord(payload)
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)

  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )

  const customers = computed(() => moduleQuery.customersCatalog.data?.value)
  const customersIsLoading = computed(
    () => moduleQuery.customersCatalog.isLoading.value,
  )

  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )

  const isLoadingUpload = computed(
    () => moduleQuery.actionUploadPo.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    isLoading,
    customers,
    customersIsLoading,
    customerFilters,
    isLoadingCategories,
    catalogs,
    modulePagination: toReactive(modulePagination),
    isLoadingUpload,
    onUploadPO,
    onRequestCatalog,
    onAction,
    onDelete,
    onDeleteMany,
    onEdit,
    onAdd,
    onUpdateProject,
  }
})

export { useProvideModule, useModule }
